import { ColumnFilter } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo } from 'react';

import { TablePrefilterProvider } from '@work4all/components/lib/components/table/TablePrefilterProvider';

import { CustomFieldsConfigProvider } from '@work4all/data/lib/custom-fields';
import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { OnOpenMask } from '../../../../../../containers/mask-overlays/mask-overlay';
import {
  OpenMaskOverride,
  useOpenMask,
} from '../../../../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import schema from '../../../../../data-tables/schemata/contract-table-schema.json';
import {
  EntityTableV8,
  IEntityTableV8,
} from '../../../../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../../../../entity-table/use-entity-data-table-v8';
import { ErpRessourceClassCell } from '../../../../ErpRessourceClassCell';
import { useErpPlanningColumns } from '../../../../planning-columns/use-erp-planning-columns';
import { TableAreas } from '../../../../table/Table';
import { useErpHandlersTemplateV8 } from '../../../../use-erp-handlers-template-v8';
import { useTableHandlersV8 } from '../../../../use-table-handlers-v8';

import { useProjectWithProcesses } from './project-planning-view/hooks/use-project-with-processes';

type ProjectContractsTableProps = {
  projectIdList?: number[];
};

type ProjectContractsTableInnerProps = IEntityTableV8<Contract> & {
  onOpenMask: OnOpenMask;
  columnFilters: ColumnFilter[];
  secondarySort?: { field: string; direction: SortDirection }[];
};

const defaultSort = [
  { field: 'contractDate', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'contractNumber', direction: SortDirection.DESCENDING },
];

export function ProjectContractsTable(props: ProjectContractsTableProps) {
  const { projectIdList } = props;

  const onOpenMask = useOpenMask();

  const prefilter = useMemo(
    () => [{ projectId: { $in: projectIdList } }],
    [projectIdList]
  );

  const { data: projectList } = useProjectWithProcesses(projectIdList);

  const columnFilters: ColumnFilter[] = useMemo(() => {
    return [
      {
        id: 'project.id',
        value: {
          value: projectList.map((pr) => ({
            id: pr.id,
            lockInhouseServices: 0,
            lockExternalServices: 0,
          })),
          readOnly: true,
          filterType: 'picker',
        },
      },
    ];
  }, [projectList]);

  return (
    <CustomFieldsConfigProvider entity={Entities.contract}>
      <TablePrefilterProvider value={{ prefilter }}>
        <OpenMaskOverride value={{ onOpenMask }}>
          <ProjectContractsTableInner
            onOpenMask={onOpenMask}
            prefilter={prefilter}
            defaultSort={defaultSort}
            secondarySort={secondarySort}
            columnFilters={columnFilters}
          />
        </OpenMaskOverride>
      </TablePrefilterProvider>
    </CustomFieldsConfigProvider>
  );
}

function ProjectContractsTableInner(props: ProjectContractsTableInnerProps) {
  const { activeTenant } = useTenant();

  const ressourceClassColumns = useErpPlanningColumns({
    entity: Entities.contract,
  });

  const schemaPatched = useMemo(() => {
    return {
      ...schema,
      columns: [...schema.columns, ...ressourceClassColumns].map((column) => {
        const shouldDisableColumn =
          column.id === 'project.id' || column.id === 'project.number';

        return {
          ...column,
          filterable: {
            ...column.filterable,
            ...(shouldDisableColumn && { disabled: true }),
            primary: false,
          },
          ...(shouldDisableColumn && { disableColumnVisibility: true }),
        };
      }),
      card: null,
    };
  }, [ressourceClassColumns]);

  const dataTable = useEntityDataTableV8<Contract, never>({
    schema: schemaPatched as never,
    listEntityType: Entities.contract,
    cells: {
      ErpRessourceClass: ErpRessourceClassCell,
    },
    prefilter: props.prefilter,
    defaultSort: props.defaultSort,
    secondarySort: props.secondarySort,
    layout: 'table',
    enableFooter: true,
    forceRequestFields: {
      positionList: [
        {
          id: null,
          unit: null,
          amount: null,
          article: {
            id: null,
            name: null,
            number: null,
            ressourceClassId: null,
          },
        },
      ],
    } as Contract,
    rowModifiers(value) {
      return {
        isFaded: value.isClosed && value.frozen,
        isShade1: !value.isClosed && value.frozen,
      };
    },
    enableMultiRowSelection: false,
    enableURLState: false,
  });

  useEffect(() => {
    const currentFilters = dataTable.table.getState()
      .columnFilters as ColumnFilter[];
    if (isEqual(currentFilters, props.columnFilters)) return;
    dataTable.table.setColumnFilters(props.columnFilters);
  }, [dataTable.table, props.columnFilters]);

  const template = useErpHandlersTemplateV8(dataTable.table);

  const tableHandlers = useTableHandlersV8(
    dataTable.table,
    Entities.contract,
    props.onOpenMask,
    template
  );

  const areas: TableAreas = { left: undefined };
  const overrides: Partial<IEntityTableV8<Contract>> = {
    actions: {
      add: null,
      convert: null,
      groupingEnabled: null,
      edit: tableHandlers.edit,
      openInNewTab: {
        getPath: (id: string) =>
          `t-${activeTenant}/more/entity/contract/details/${id}`,
      },
      custom: {
        left: undefined,
      },
    },
    listEntityType: Entities.contract,
    layout: 'table',
  };

  return (
    <EntityTableV8
      template={template}
      areas={areas}
      displayFooter={false}
      hideMoreButton
      overrides={overrides}
      {...props}
      {...dataTable}
    />
  );
}
